import React from 'react'

import { AcornsIcon } from '@acorns/icons'
import { Button, TooltipV2, theme } from '@acorns/web-components'
import { useRemoteStrings } from '@acorns/web-utils'
import { useAnalyticsClient } from '@acorns/web-utils'
import styled from 'styled-components'

import { useOutsideContributionCodeByInvestmentAccountIdQuery } from 'generated/types'
import {
  GIFTS_DETAIL_NO_GIFTS_BUTTON,
  GIFTS_DETAIL_NO_GIFTS_TITLE,
} from 'utils/testing/testIDs'

import { ACTIONS, shareAnalyticsOptions } from '../../analytics'
import useTooltip from '../../hooks/useTooltip'
import { Title } from '../../shared/SectionDetails'
import { fonts } from '../../styles'

const ThanksText = styled.p<{ hasGifts: boolean }>`
  ${fonts.AN_14_600_22}
  color: ${theme.colors.beige};
  margin-bottom: ${(props) => (props.hasGifts ? 10 : 20)}px;
  width: 100%;
`

const StyledTooltip = styled(TooltipV2)`
  background-color: ${theme.colors.white};
  color: ${theme.colors.charcoal};

  & > div:first-child:after {
    border-top-color: ${theme.colors.white};
  }
`

// To anchor the tooltip in the proper position
const Wrapper = styled.div`
  width: 140px;
`

const CopyLinkText = styled.p`
  ${fonts.AN_14_600_22}
  color: ${theme.colors.slate};
  line-height: 24px;
  margin-right: 7px;
`

const NoGift = ({
  beneficiaryName,
  investmentAccountId,
}: {
  beneficiaryName: string
  investmentAccountId: string
}): React.ReactElement => {
  const analytics = useAnalyticsClient()
  const { getRemoteString } = useRemoteStrings()
  const { showTooltip, setShowTooltip } = useTooltip()
  const { data, loading } =
    useOutsideContributionCodeByInvestmentAccountIdQuery({
      variables: {
        investmentAccountId,
      },
    })

  const url = data?.outsideContributionCodeByInvestmentAccountId?.url
  const hasGiftLink = Boolean(url) && !loading

  const handleCopy = () => {
    if (hasGiftLink) {
      navigator.clipboard.writeText(url as string)
      setShowTooltip(true)

      analytics.track(ACTIONS.BUTTON_TAPPED, {
        ...shareAnalyticsOptions,
        event_description:
          'Early Quarterly Recaps Outside Contribution Link Copy Tapped',
        object_name: 'earlyQuarterlyRecapOutsideContributionLinkCopy',
      })
    }
  }

  return (
    <>
      <Title data-testid={GIFTS_DETAIL_NO_GIFTS_TITLE}>
        {getRemoteString({ key: 'early.recap.gifts.no_gifts.header' })}
      </Title>
      {hasGiftLink ? (
        <ThanksText hasGifts={false}>
          {getRemoteString({
            key: 'early.recap.gifts.no_gifts.subheader.variable',
            variables: [beneficiaryName],
          })}
        </ThanksText>
      ) : (
        <ThanksText hasGifts={false}>
          {getRemoteString({
            key: 'early.recap.gifts.no_gifts.subheader.public',
          })}
        </ThanksText>
      )}
      {hasGiftLink ? (
        <Wrapper>
          <StyledTooltip
            content={getRemoteString({
              key: 'early.recap.gifts.no_gifts.tooltip',
            })}
            placement="top"
            visibleOverride={showTooltip}
          >
            <Button
              aria-label="Copy Link to Share Early Gifts Page"
              testId={GIFTS_DETAIL_NO_GIFTS_BUTTON}
              onPress={handleCopy}
              type="light"
            >
              <CopyLinkText>
                {getRemoteString({ key: 'early.recap.gifts.no_gifts.cta' })}
              </CopyLinkText>
              <AcornsIcon
                icon={AcornsIcon.Icon.UtilityShareLinkFv}
                width={24}
              />
            </Button>
          </StyledTooltip>
        </Wrapper>
      ) : null}
    </>
  )
}

export { NoGift }
